/**
 * Chunk C — Training step (rewritten for an honest watched gate).
 *
 * Issues this rewrite fixes vs. the first cut:
 *   • YouTube iframes were auto-rendered → `onLoad` fired instantly → every
 *     video was marked watched on page mount. Now the player loads only on
 *     explicit "Load video" click and "Mark as watched" stays disabled until
 *     a per-video dwell-timer elapses.
 *   • Submit was impossible when admin shipped a quiz-less or video-less
 *     training. We now allow both paths independently.
 *   • Player + dwell state survives quiz retries — only quiz answers reset
 *     on retry, not the videos.
 *
 * Auth: hits /api/training-content (no answer key for non-admins) and
 *       /api/auth/complete-training on submit.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    GraduationCap, PlayCircle, CheckCircle2, ArrowRight,
    AlertTriangle, RotateCcw, Clock, Lock,
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import { useAuth, routeForStep } from '../../store/auth';
import { toEmbedUrl, extractYoutubeId, youtubeThumbnailUrl } from '../../utils/youtube';

/** Fisher–Yates shuffle (mutates copy). */
function shuffleArray(arr) {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
}

/** Randomise choice order; keep a map so submits use server indices. */
function shuffleQuestionChoices(q) {
    const indexed = (q.choices || []).map((text, originalIndex) => ({ text, originalIndex }));
    const shuffled = shuffleArray(indexed);
    return {
        question: q.question,
        choices: shuffled.map(x => x.text),
        toOriginalIndex: shuffled.map(x => x.originalIndex),
    };
}

export default function Training() {
    const loadMe = useAuth(s => s.loadMe);
    const nav    = useNavigate();

    const [doc, setDoc]         = useState(null);
    const [loadErr, setLoadErr] = useState('');
    const [watched, setWatched] = useState({});   // { [idx]: true }
    const [answers, setAnswers] = useState({});   // { [qIdx]: choiceIdx }
    const [wrong, setWrong]     = useState([]);
    const [busy, setBusy]       = useState(false);
    const [result, setResult]   = useState(null);
    const [shuffleKey, setShuffleKey] = useState(0);

    useEffect(() => {
        let alive = true;
        setLoadErr('');
        api.get('/training-content')
            .then(async (r) => {
                if (!alive) return;
                const data = r.data;
                setDoc(data);
                if (data?.hasContent === false) {
                    await api.post('/auth/complete-training', { answers: [] });
                    const u = await loadMe();
                    nav(routeForStep(u));
                }
            })
            .catch(e => {
                if (!alive) return;
                setLoadErr(e?.response?.data?.error || 'Could not load training content');
            });
        return () => { alive = false; };
    }, [loadMe, nav]);

    const videos = doc?.videos || [];
    const quiz   = doc?.quiz   || [];
    const minWatchSec = Math.max(0, Number(doc?.minWatchSec) || 0);

    /* New order on every page load / refresh (and on quiz retry). */
    const shuffledQuiz = useMemo(
        () => quiz.map(shuffleQuestionChoices),
        [quiz, shuffleKey]
    );

    const requiredVideos = useMemo(
        () => videos.map((v, i) => ({ ...v, i })).filter(v => v.required),
        [videos]
    );

    /* Submission gates — handle every shape admins can ship:
         • videos + quiz   → both required
         • videos only     → just watch
         • quiz only       → just answer
         • neither         → can't submit (admin must configure something) */
    const allWatched   = requiredVideos.length === 0 || requiredVideos.every(v => watched[v.i]);
    const allAnswered  = shuffledQuiz.length === 0 || shuffledQuiz.every((_, i) => answers[i] !== undefined);
    const hasAnyContent = videos.length > 0 || shuffledQuiz.length > 0;
    const canSubmit    = hasAnyContent && allWatched && allAnswered && !busy;

    const submit = async () => {
        if (!canSubmit) return;
        setBusy(true);
        setWrong([]);
        setResult(null);
        try {
            /* Server expects one answer per quiz question. If there's no
               quiz it still validates the array length, so we send []. */
            const arr = shuffledQuiz.map((q, i) => Number(q.toOriginalIndex[answers[i]]));
            const { data } = await api.post('/auth/complete-training', { answers: arr });
            setResult(data);
            toast.success('Training complete ✓');
            const u = await loadMe();
            nav(routeForStep(u));
        } catch (e) {
            const d = e?.response?.data;
            if (d && d.passed === false) {
                setWrong(d.wrong || []);
                setResult(d);
                toast.error(d.message || 'Some answers were wrong — try again.');
            } /* else api interceptor toasted it */
        } finally { setBusy(false); }
    };

    const retryQuiz = () => {
        setAnswers({});
        setWrong([]);
        setResult(null);
        setShuffleKey(k => k + 1);
        /* Note: we intentionally do NOT reset `watched`. The dwell-time
           gate already cost the user effort the first time; making them
           redo it after a wrong quiz answer would feel punitive. */
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    if (loadErr) {
        return (
            <div className="ag-card p-10 text-center space-y-3">
                <AlertTriangle className="h-8 w-8 text-amber-600 mx-auto" />
                <p className="text-sm text-ink-700">{loadErr}</p>
                <button type="button" className="ag-btn-ghost text-sm" onClick={() => window.location.reload()}>
                    Retry
                </button>
            </div>
        );
    }

    if (!doc) {
        return <div className="ag-card p-10 text-center text-sm text-ink-500">Loading training…</div>;
    }

    return (
        <div className="space-y-6">
            <div className="text-center">
                <div className="h-14 w-14 rounded-full bg-brand-teal-50 text-brand-teal-700 grid place-items-center mx-auto">
                    <GraduationCap className="h-7 w-7" />
                </div>
                <h1 className="text-2xl font-extrabold mt-4">Mandatory training</h1>
                <p className="text-sm text-ink-500 mt-2 max-w-md mx-auto">
                    {videos.length > 0 && shuffledQuiz.length > 0
                        ? 'Watch every required video, then answer the quiz. You have unlimited attempts.'
                        : videos.length > 0
                          ? 'Watch the required videos to continue. Unlimited attempts.'
                          : shuffledQuiz.length > 0
                            ? 'Answer the quiz to continue. Unlimited attempts.'
                            : 'No training content is configured yet. Ask an admin to set it up.'}
                </p>
                <span className="inline-flex items-center gap-1 mt-3 text-[10px] uppercase tracking-wider font-bold rounded-full bg-ink-100 text-ink-500 px-2.5 py-1">
                    Version {doc.version}
                </span>
            </div>

            {doc.introHtml && (
                <div className="ag-card p-5">
                    <div className="ag-onboard-html" dangerouslySetInnerHTML={{ __html: doc.introHtml }} />
                </div>
            )}

            {/* Empty content — surface a clear blocker so users don't spin */}
            {!hasAnyContent && (
                <div className="ag-card p-6 text-center bg-amber-50 border border-amber-200">
                    <Lock className="h-7 w-7 text-amber-700 mx-auto" />
                    <h2 className="font-bold text-amber-800 mt-2">No training content yet</h2>
                    <p className="text-xs text-amber-700 mt-1">
                        Your admin hasn't added videos or quiz questions. This step will unlock automatically
                        once they do — refresh in a few minutes.
                    </p>
                </div>
            )}

            {/* Videos */}
            {videos.length > 0 && (
                <div className="space-y-4">
                    {videos.map((v, i) => (
                        <VideoCard
                            key={`${i}-${doc.version}`}
                            idx={i}
                            video={v}
                            minWatchSec={minWatchSec}
                            watched={!!watched[i]}
                            onWatched={() => setWatched(w => ({ ...w, [i]: true }))}
                        />
                    ))}
                </div>
            )}

            {/* Quiz */}
            {shuffledQuiz.length > 0 && (
                <div className="ag-card p-5 space-y-5">
                    <div>
                        <h2 className="font-bold text-lg">Quiz</h2>
                        <p className="text-xs text-ink-500 mt-1">
                            Pick the best answer for each question. You need{' '}
                            {doc.passingScore > 0 ? doc.passingScore : shuffledQuiz.length} of {shuffledQuiz.length} correct to pass.
                        </p>
                    </div>

                    {shuffledQuiz.map((q, qi) => (
                        <QuizQuestion
                            key={`${qi}-${shuffleKey}`}
                            qIndex={qi}
                            q={q}
                            picked={answers[qi]}
                            isWrong={wrong.includes(qi)}
                            onPick={(ci) => {
                                setAnswers(a => ({ ...a, [qi]: ci }));
                                if (wrong.includes(qi)) setWrong(w => w.filter(x => x !== qi));
                            }}
                        />
                    ))}

                    {result && result.passed === false && (
                        <div className="rounded-lg p-3 text-sm bg-amber-50 text-amber-800 ring-1 ring-amber-200 flex items-start gap-2">
                            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                            <div>
                                <b>{result.correct} of {result.total} correct.</b> You need {result.passingScore} to pass.
                                Try the highlighted questions again — there's no penalty for retrying.
                            </div>
                        </div>
                    )}
                </div>
            )}

            <div className="flex flex-col sm:flex-row gap-2 sm:items-center sm:justify-between">
                <div className="text-[11px] text-ink-500">
                    {!hasAnyContent ? ' '
                        : allWatched && allAnswered
                            ? <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle2 className="h-3.5 w-3.5" /> Ready to submit.</span>
                            : !allWatched
                                ? `Watch all ${requiredVideos.length} required video${requiredVideos.length === 1 ? '' : 's'} to enable Submit.`
                                : 'Answer every question to enable Submit.'}
                </div>
                <div className="flex gap-2">
                    {result && result.passed === false && (
                        <button onClick={retryQuiz} className="ag-btn-ghost">
                            <RotateCcw className="h-4 w-4" /> Clear answers
                        </button>
                    )}
                    <button onClick={submit} disabled={!canSubmit} className="ag-btn-primary">
                        {busy ? 'Submitting…' : <>Submit &amp; continue <ArrowRight className="h-4 w-4" /></>}
                    </button>
                </div>
            </div>
        </div>
    );
}

/* ─────────── Video player card ───────────
 * YouTube clips render immediately so trainees see the configured video.
 * MP4 / other sources still use an explicit "Load video" click.
 * Dwell timer gates "Mark as watched" — we never auto-mark on iframe load. */
function VideoCard({ idx, video, minWatchSec, watched, onWatched }) {
    const youtubeId = extractYoutubeId(video.url);
    const embed = toEmbedUrl(video.url);
    const isYouTubeEmbed = !!youtubeId;
    const thumb = youtubeThumbnailUrl(youtubeId);

    const [loaded, setLoaded] = useState(isYouTubeEmbed);
    const [remaining, setRemaining] = useState(0);
    const tickRef = useRef(null);

    /* Once the player loads, start a per-video dwell countdown so users can't
       just open + immediately click "Mark watched". 0 = no gate (admin opted
       out via settings). */
    useEffect(() => {
        if (!loaded || watched) return;
        const cap = Math.min(minWatchSec || 0, Math.max(0, Number(video.durationSec) || 60));
        setRemaining(cap);
        if (cap <= 0) return;
        tickRef.current = setInterval(() => {
            setRemaining((s) => {
                if (s <= 1) {
                    clearInterval(tickRef.current);
                    tickRef.current = null;
                    return 0;
                }
                return s - 1;
            });
        }, 1000);
        return () => {
            if (tickRef.current) clearInterval(tickRef.current);
            tickRef.current = null;
        };
    }, [loaded, minWatchSec, video.durationSec, watched]);

    const canMarkWatched = loaded && remaining === 0 && !watched;

    return (
        <div className="ag-card overflow-hidden">
            <div className="px-5 py-3 flex items-center justify-between gap-3 border-b border-ink-100">
                <div className="flex items-center gap-2 min-w-0">
                    <span className="text-[10px] font-bold rounded-full bg-brand-teal-50 text-brand-teal-700 px-2 py-0.5 shrink-0">{idx + 1}</span>
                    <h3 className="font-bold truncate">{video.title}</h3>
                    {video.required && <span className="text-[10px] font-bold rounded-full bg-amber-50 text-amber-700 px-2 py-0.5 shrink-0">required</span>}
                </div>
                <span className={`inline-flex items-center gap-1 text-[11px] font-semibold shrink-0
                    ${watched ? 'text-emerald-700' : 'text-ink-500'}`}>
                    {watched
                        ? <><CheckCircle2 className="h-3.5 w-3.5" /> Watched</>
                        : <><PlayCircle  className="h-3.5 w-3.5" /> Not watched</>}
                </span>
            </div>

            {loaded ? (
                <div className="aspect-video bg-ink-100">
                    {isYouTubeEmbed ? (
                        <iframe
                            src={embed}
                            title={video.title}
                            allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                            allowFullScreen
                            className="w-full h-full"
                        />
                    ) : (
                        <video
                            src={video.url}
                            controls
                            className="w-full h-full"
                            onEnded={() => { if (!watched) onWatched(); }}
                        />
                    )}
                </div>
            ) : (
                <button
                    type="button"
                    onClick={() => setLoaded(true)}
                    className="aspect-video w-full bg-ink-100 relative overflow-hidden grid place-items-center text-ink-500 hover:bg-ink-100/70 gap-1.5"
                >
                    {thumb && (
                        <img
                            src={thumb}
                            alt=""
                            className="absolute inset-0 w-full h-full object-cover opacity-80"
                        />
                    )}
                    <span className="relative z-10 flex flex-col items-center gap-1.5">
                        <PlayCircle className="h-10 w-10" />
                        <span className="text-xs font-medium">Load video</span>
                    </span>
                </button>
            )}

            <div className="px-5 py-3 flex items-center justify-between gap-3">
                <span className="text-[11px] text-ink-500 inline-flex items-center gap-1">
                    {video.durationSec ? <><Clock className="h-3 w-3" /> {Math.max(1, Math.round(video.durationSec / 60))} min</> : '—'}
                </span>

                {!watched && (
                    loaded
                        ? (
                            <button
                                type="button"
                                onClick={onWatched}
                                disabled={!canMarkWatched}
                                className={`text-xs font-semibold transition
                                    ${canMarkWatched
                                        ? 'text-brand-teal-700 hover:underline'
                                        : 'text-ink-500 cursor-not-allowed'}`}
                            >
                                {canMarkWatched
                                    ? 'Mark as watched'
                                    : <>Watch for {remaining}s to unlock…</>}
                            </button>
                        )
                        : (
                            <span className="text-[11px] text-ink-500">Click <b>Load video</b> to begin</span>
                        )
                )}
            </div>
        </div>
    );
}

/* ─────────── Quiz question ─────────── */
function QuizQuestion({ qIndex, q, picked, isWrong, onPick }) {
    const shell = isWrong
        ? 'border-amber-300 bg-amber-50/40'
        : 'border-ink-200 bg-white';
    const header = isWrong
        ? 'border-amber-200 bg-amber-50/60'
        : 'border-ink-100 bg-ink-50/40';

    return (
        <div className={`rounded-xl border overflow-hidden transition ${shell}`}>
            <div className={`px-4 py-3 border-b ${header}`}>
                <div className="text-sm font-semibold flex items-start gap-2">
                    <span className={`text-[10px] font-bold rounded-full px-2 py-0.5 shrink-0
                        ${isWrong ? 'bg-amber-100 text-amber-800' : 'bg-white text-ink-700 ring-1 ring-ink-200'}`}>
                        Q{qIndex + 1}
                    </span>
                    <span className="leading-snug pt-0.5">{q.question}</span>
                </div>
            </div>
            <div className="p-4 space-y-1.5">
                {q.choices.map((c, ci) => {
                    const active = picked === ci;
                    return (
                        <label key={ci}
                            className={`flex items-start gap-3 p-2.5 rounded-lg border cursor-pointer transition
                                ${active ? 'border-brand-teal/60 bg-brand-teal-50/60'
                                         : 'border-ink-200 hover:border-ink-300 bg-white'}`}>
                            <input
                                type="radio"
                                name={`q-${qIndex}`}
                                value={ci}
                                checked={active}
                                onChange={() => onPick(ci)}
                                className="mt-0.5 h-4 w-4 accent-brand-teal"
                            />
                            <span className="text-sm">{c}</span>
                        </label>
                    );
                })}
            </div>
        </div>
    );
}
